import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import moment from "moment";

import PrimaryButton from "../../Components/Buttons/primary-button";
import AmountField from "../../Components/Form Fields/amount-field";
import SelectField from "../../Components/Form Fields/select-field";
import TextField from "../../Components/Form Fields/text-field";
import DatePickerField from "../../Components/Form Fields/date-picker-field";
import { useConsumerDealsData } from "../Deals";

import renderStakeCheckingOnboardingStep from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";
import renderAddPayee from "../../Organisms/Modals/Pay Rent/add-payee";
import renderSelectDestinationAccount from "../../Organisms/Modals/Pay Rent/select-destination-account";
import renderConfirmPayment from "../../Organisms/Modals/Pay Rent/confirm-payment";
import renderRentPaymentDetails from "../../Organisms/Modals/Pay Rent/rent-payment-details";
import renderCancelRentPayment from "../../Organisms/Modals/Pay Rent/cancel-rent-payment";

import withFormProvider from "../../HOC/withFormProvider";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import { useModalContext } from "../../Contexts/ModalContext";

import { useGetPayees, useRecurringRentPayments, useRentPayments, useStakeDebit } from "../../CustomHooks/Queries";

import { validation } from "../../Utils/validations";
import {
  capitaliseString,
  formatCurrency,
  formatDate,
  getNumberWithOrdinal,
  getRecurringStartDate,
  isSVDApproved,
  isSVDEligible,
} from "../../Utils/helpers";
import { longMonthDateFormat, weekdayNames } from "../../Utils/constants";

import { ArrowLeftCircle, Swap } from "../../Assets/Icons";

import "./rent-payment.scss";
import "./rent-payment-responsive.scss";

const payRentScreens = {
  payRent: "pay-rent",
  transactionHistory: "transaction-history",
};

export const frequencyOptions = [
  { label: "Weekly", value: "weekly" },
  { label: "Biweekly", value: "biweekly" },
  { label: "Monthly", value: "monthly" },
];

const weeklyOptions = weekdayNames.map((weekday) => ({ label: weekday, value: weekday.toLowerCase() }));

const monthlyOptions = Array.from(new Array(28)).map((_, idx) => ({
  label: idx + 1,
  value: String(idx + 1),
}));

export const durationOptions = [
  { label: "Until I Cancel", value: "until i cancel" },
  { label: "Until a Specific Number of Payments", value: "until specific number of payments" },
  { label: "Until a Specific Date", value: "until specific date" },
];

const NoOfPaymentField = () => {
  const { register, getFieldState, formState } = useFormContext();

  const noOfPaymentsProps = register("noOfPayments", { required: "No of payments is required" });
  const { error: noOfPaymentsFieldError } = getFieldState(noOfPaymentsProps.name, formState);

  return (
    <TextField
      className="filled mb-4"
      label="Number of Payments"
      type="number"
      inputProps={{ min: 0, max: 120 }}
      {...noOfPaymentsProps}
      error={!!noOfPaymentsFieldError}
      errorMessage={noOfPaymentsFieldError?.message}
    />
  );
};

const RecurringFields = () => {
  const { register, watch, setValue, getFieldState, formState } = useFormContext();

  const frequency = watch("frequency") ?? "";
  const recurringDay = watch("recurringDay") ?? "";
  const duration = watch("duration") ?? "";
  const recurringEndDate = watch("recurringEndDate");

  const frequencyProps = register("frequency", { required: "Frequency is required" });
  const recurringDayProps = register("recurringDay", { required: "Recurring Day is required" });
  const durationProps = register("duration", { required: "Duration is required" });

  const { error: frequencyFieldError } = getFieldState(frequencyProps.name, formState);
  const { error: recurringDayFieldError } = getFieldState(recurringDayProps.name, formState);
  const { error: durationFieldError } = getFieldState(durationProps.name, formState);

  const dayOptions = useMemo(() => (frequency === "monthly" ? monthlyOptions : weeklyOptions), [frequency]);

  const recurringStartDate = useMemo(() => getRecurringStartDate(frequency, recurringDay), [frequency, recurringDay]);

  useEffect(() => {
    if (!!recurringDay && !dayOptions.find((option) => option.value === recurringDay)) {
      setValue("recurringDay", null);
    }

    if (!!recurringStartDate && !!recurringEndDate && moment(recurringEndDate).isBefore(recurringStartDate, "day")) {
      setValue("recurringEndDate", null);
    }
  }, [recurringDay, recurringStartDate, recurringEndDate, dayOptions, setValue]);

  return (
    <>
      <Row className="row-gap-4 mb-4">
        <Col sm={6}>
          <SelectField
            className="filled"
            label="Frequency"
            options={frequencyOptions}
            {...frequencyProps}
            value={frequency ?? ""}
            error={!!frequencyFieldError}
            errorMessage={frequencyFieldError?.message}
          />
        </Col>
        <Col sm={6}>
          <SelectField
            className="filled"
            label="Day"
            options={dayOptions}
            value={recurringDay ?? ""}
            {...recurringDayProps}
            error={!!recurringDayFieldError}
            errorMessage={recurringDayFieldError?.message}
          />
        </Col>
      </Row>
      <SelectField
        className="filled mb-4"
        label="Duration"
        options={durationOptions}
        {...durationProps}
        value={duration ?? ""}
        error={!!durationFieldError}
        errorMessage={durationFieldError?.message}
      />
      {duration === durationOptions[1].value && <NoOfPaymentField />}
      {duration === durationOptions[2].value && (
        <div className="mb-4">
          <DatePickerField
            variant="filled"
            name="recurringEndDate"
            label="End Date"
            textFieldProps={{ placeholder: "", fullWidth: true }}
            datePickerProps={{
              minDate: !!recurringStartDate ? moment(recurringStartDate) : undefined,
            }}
            validations={{
              isAValidDate: validation.date.isAValidDate,
            }}
            required="End Date is required"
          />
        </div>
      )}
    </>
  );
};

function RentPayment({ isRecurringTransaction = false }) {
  const [renderScreen, setRenderScreen] = useState(payRentScreens.payRent);
  const [editTransaction, setEditTransaction] = useState(null);

  const navigate = useNavigate();
  const { watch, reset: resetForm, setValue, handleSubmit } = useFormContext();

  const cashAmount = parseFloat(watch("cash-amount")?.unmasked ?? "");
  const frequency = watch("frequency");
  const recurringDay = watch("recurringDay");

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  const { isLoading: isLoadingPayees, data: payees } = useGetPayees({
    onSuccess: (data) => !selectedPayee && setSelectedPayee(data?.[0] ?? null),
  });

  const setupRenderScreen = (transactions, transactionIdToEdit) => {
    if (transactions?.length > 0 && !transactionIdToEdit && !editTransaction) {
      setRenderScreen(payRentScreens.transactionHistory);
    } else if (isRecurringTransaction && !!transactionIdToEdit) {
      resetForm();
      const selectedTransaction = transactions?.find((transaction) => transaction.id === parseInt(transactionIdToEdit));
      setValue("cash-amount", {
        masked: formatCurrency(selectedTransaction?.amount),
        unmasked: selectedTransaction?.amount,
      });
      setValue("duration", selectedTransaction?.duration);
      setValue("recurringEndDate", selectedTransaction?.end_date?.split("T")[0]);
      setValue("noOfPayments", selectedTransaction?.number_of_payments);
      setValue("frequency", selectedTransaction?.frequency_type);
      setValue("recurringDay", selectedTransaction?.frequency_day);
      setRenderScreen(payRentScreens.payRent);
    } else if (!editTransaction) {
      setRenderScreen(payRentScreens.payRent);
    }
  };

  const { isFetching: isFetchingRentPayments, data: rentPayments } = useRentPayments(
    { status: "Pending", type: "one-time" },
    {
      enabled: !isRecurringTransaction,
      onSuccess: (t) => setupRenderScreen(t),
    },
  );

  const { isFetching: isFetchingRecurringRentPayments, data: recurringRentPayments } = useRecurringRentPayments({
    enabled: isRecurringTransaction,
    onSuccess: (t) => setupRenderScreen(t),
  });

  const { isLoadingConsumerDealsData, stakeSaverOffer, isStakeSaverActive } = useConsumerDealsData();

  const [selectedPayee, setSelectedPayee] = useState(payees?.[0] ?? null);

  const IsSVDApproved = useMemo(() => isSVDApproved(stakeDebit?.data), [stakeDebit?.data]);

  const recurringStartDate = useMemo(() => getRecurringStartDate(frequency, recurringDay), [frequency, recurringDay]);

  const transferAmountLabel = useMemo(() => {
    if (!IsSVDApproved) return "Activate Stake Checking";
    else if (!!stakeSaverOffer && !isStakeSaverActive) return "Join Stake Saver";
    else return `${isRecurringTransaction ? (!!editTransaction ? "Update" : "Schedule") : "Make"} Payment`;
  }, [stakeSaverOffer, isStakeSaverActive, IsSVDApproved, isRecurringTransaction, editTransaction]);

  const handleBackButtonClick = () => {
    if (
      renderScreen === payRentScreens.payRent &&
      (isRecurringTransaction ? recurringRentPayments?.length > 0 : rentPayments?.length > 0)
    ) {
      resetForm();
      setRenderScreen(payRentScreens.transactionHistory);
      !!editTransaction && setEditTransaction(null);
    } else {
      navigate(-1);
    }
  };

  const handleViewDetailsClick = (transaction) => {
    if (isRecurringTransaction) {
      navigate(`/cash/recurring-payment/view/${transaction?.id}`);
    } else {
      renderRentPaymentDetails(modalContext, {
        paymentDetails: transaction,
      });
    }
  };

  const handleTransactionPrimaryAction = (transaction) => {
    if (isRecurringTransaction) {
      setEditTransaction(transaction);
      setupRenderScreen(recurringRentPayments, transaction?.id);
    } else {
      renderCancelRentPayment(modalContext, transaction);
    }
  };

  const onSubmit = (recurringFieldValues) => {
    if (!IsSVDApproved) {
      if (isSVDEligible(stakeDebit?.data)) {
        renderStakeCheckingOnboardingStep(modalContext);
      } else {
        stakeCheckingStatusAction(stakeDebit?.data);
      }
    } else if (!!stakeSaverOffer && !stakeSaverOffer?.is_activated) {
      navigate("/deals/stake-saver");
    } else {
      renderConfirmPayment(modalContext, {
        cashAmount,
        selectedPayee,
        isRecurringTransaction,
        recurringFieldValues,
        editTransaction,
        onConfirm: () => {
          resetForm();
          if (!!editTransaction) {
            handleBackButtonClick();
          }
        },
      });
    }
  };

  const disableSubmitButton =
    IsSVDApproved && (!stakeSaverOffer || stakeSaverOffer?.is_activated) && (!selectedPayee || !cashAmount);

  const isLoading =
    isLoadingPayees ||
    isLoadingStakeDebit ||
    isLoadingConsumerDealsData ||
    isFetchingRentPayments ||
    isFetchingRecurringRentPayments;

  return (
    <div className="add-cash">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={handleBackButtonClick} aria-label="Go back">
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">
                {!isRecurringTransaction ? "One-Time Payment" : `${!!editTransaction ? "Edit " : ""}Recurring Payment`}
              </h3>
              <div className="dummy"></div>
            </div>

            {isLoading ? (
              <div className="loading-content">
                <CircularProgress />
              </div>
            ) : renderScreen === payRentScreens.transactionHistory ? (
              <div className="content">
                <div className="top-container">
                  {(isRecurringTransaction ? recurringRentPayments : rentPayments)?.map((transaction, idx) => {
                    return (
                      <div key={idx} className="rent-payment-card">
                        <div className="payee-card">
                          <div className="account-badge">{transaction?.payeeAcronym}</div>
                          <div className="info-container">
                            <p className="title">{transaction?.to}</p>
                            <p className="description">{transaction?.payee}</p>
                          </div>
                          <div className="transfer-info">
                            <div className="transfer-value">{formatCurrency(transaction?.total)}</div>
                            {isRecurringTransaction ? (
                              <div className="text-base-regular color--american-silver">
                                {capitaliseString(transaction?.frequency_type)} on{" "}
                                {transaction?.frequency_type === frequencyOptions[2].value
                                  ? `the ${getNumberWithOrdinal(transaction?.frequency_day)}`
                                  : capitaliseString(transaction?.frequency_day)}
                              </div>
                            ) : (
                              <div className={`transfer-status-badge ${transaction?.statusType}`}>
                                <transaction.StatusIcon /> {transaction?.statusDisplayName}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="payment-details">
                          <div className="transfer-detail py-0">
                            <span className="detail-label">
                              {isRecurringTransaction ? "Next Payment Date" : "Date"}
                            </span>
                            <span className="detail-value">
                              {formatDate(
                                isRecurringTransaction ? transaction?.next_payment_date : transaction?.created_at,
                                longMonthDateFormat,
                              )}
                            </span>
                          </div>
                          <div className="transfer-detail py-0">
                            <span className="detail-label">
                              {isRecurringTransaction ? "Last Payment Date" : "Estimated Arrival"}
                            </span>
                            <span className="detail-value">
                              {isRecurringTransaction && transaction?.duration === durationOptions[0].value
                                ? "Until Canceled"
                                : formatDate(
                                    isRecurringTransaction
                                      ? transaction?.last_payment_date
                                      : transaction?.estimated_arrival,
                                    longMonthDateFormat,
                                  )}
                            </span>
                          </div>
                          {isRecurringTransaction && !!transaction?.remaining_payments && (
                            <div className="transfer-detail py-0">
                              <span className="detail-label">Remaining Payments</span>
                              <span className="detail-value">{transaction?.remaining_payments}</span>
                            </div>
                          )}
                          <div className="action-container">
                            <PrimaryButton
                              className="secondary-colors flex-grow-1"
                              onClick={handleViewDetailsClick.bind(this, transaction)}
                            >
                              View Details
                            </PrimaryButton>
                            <PrimaryButton
                              className={`${isRecurringTransaction ? "" : "red-theme "}flex-grow-1`}
                              onClick={handleTransactionPrimaryAction.bind(this, transaction)}
                            >
                              {isRecurringTransaction ? "Edit Payment" : "Cancel Payment"}
                            </PrimaryButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="bottom-container">
                  <PrimaryButton onClick={setRenderScreen.bind(this, payRentScreens.payRent)}>
                    {!isRecurringTransaction ? "Make New Payment" : "Schedule New Payment"}
                  </PrimaryButton>
                </div>
              </div>
            ) : renderScreen === payRentScreens.payRent ? (
              <form className="content mt-sm-12 mt-6" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div className="top-container">
                  <AmountField />
                  <span className="amount-usage-label mt-2">
                    Available balance: {formatCurrency((stakeDebit?.data?.data?.available ?? 0) / 100)}
                  </span>
                </div>
                <div className="bottom-container">
                  <div className="section-header">
                    <div className="section-title">Destination Account</div>
                  </div>
                  <div className="payee-card mb-4">
                    {(!!selectedPayee || !!editTransaction) && (
                      <div className="account-badge">{selectedPayee?.nameAcronym}</div>
                    )}
                    <div className="info-container">
                      <p className="title">
                        {!selectedPayee
                          ? "No payee added"
                          : !!editTransaction
                            ? editTransaction?.to
                            : selectedPayee?.account}
                      </p>
                      <p className="description">
                        {!selectedPayee
                          ? "Please add a payee to proceed with your payment."
                          : !!editTransaction
                            ? editTransaction?.payee
                            : selectedPayee?.name}
                      </p>
                    </div>
                    {!selectedPayee ? (
                      <PrimaryButton type="button" onClick={renderAddPayee.bind(this, modalContext)}>
                        Add New Payee
                      </PrimaryButton>
                    ) : !editTransaction ? (
                      <div className="actions">
                        <button
                          className="edit"
                          type="button"
                          onClick={renderSelectDestinationAccount.bind(this, modalContext, {
                            preferredPayee: selectedPayee,
                            onChange: (payee) => {
                              setSelectedPayee(payee);
                              onModalClose();
                            },
                          })}
                          aria-label="Change payee"
                        >
                          <Swap />
                        </button>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                  {isRecurringTransaction && <RecurringFields />}
                  <div className="section-header">
                    <div className="section-title">Payment Details</div>
                  </div>
                  <div className="details">
                    <div className="detail-row">
                      <span className="detail-label">From</span>
                      <span className="detail-value">
                        Stake Checking
                        {!!stakeDebit?.data?.data?.account_number &&
                          ` · ${stakeDebit?.data?.data?.account_number?.slice(-4)}`}
                      </span>
                    </div>
                    {isRecurringTransaction && (
                      <div className="detail-row">
                        <span className="detail-label">Start Date</span>
                        <span className="detail-value">
                          {formatDate(
                            !!editTransaction ? editTransaction?.start_date : recurringStartDate,
                            longMonthDateFormat,
                          ) ?? "-"}
                        </span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Fee</span>
                      <span className="detail-value">Free</span>
                    </div>
                    <div className="detail-row">
                      <div className="info white-space-pre-line">
                        {isRecurringTransaction
                          ? "Transfers take one business day to process.\nTransfers set up on a weekend, or holiday, will occur on the next business day."
                          : "2-3 business days. Transactions made after 3:00 PM ET or on a weekend or holiday will be processed the next business day."}
                      </div>
                    </div>
                  </div>
                  <PrimaryButton className="mt-sm-6 mt-4" type="submit" disabled={disableSubmitButton}>
                    {transferAmountLabel}
                  </PrimaryButton>
                </div>
              </form>
            ) : (
              <></>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default withFormProvider(RentPayment);
